import { zodResolver } from '@hookform/resolvers/zod';
import { LINE_ITEM_TYPES, DISCOUNT_TYPES } from '@weft/types';
import type { TierDiscountConfiguration } from '@weft/types';
import type { TiersUsageMapInput } from '@weft/types';
import { useParamsDynamicInvoiceId } from 'apps/app/src/hooks/use-dynamic-invoice-id';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import { useEffect } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { z } from 'zod';
import { useUpdateLineItem } from '../../../../../hooks/requests/use-update-line-item';

const DiscountFormSchema = z.object({
  amount: z.number(),
});

type DiscountForm = z.infer<typeof DiscountFormSchema>;

type DiscountFormProps = {
  amount?: number;
  lineItemId: string;
  productVariantId: string;
  tierIndex: number;
  editMode: boolean;
  children: (discountRegister: UseFormRegisterReturn<'amount'>) => JSX.Element;
};

export const DiscountFormContainer: React.FC<DiscountFormProps> = ({ children, editMode, lineItemId, productVariantId, tierIndex, amount }) => {
  const form = useForm<DiscountForm>({
    resolver: zodResolver(DiscountFormSchema),
    defaultValues: {
      amount,
    },
  });

  const discountRegister = form.register('amount', {
    valueAsNumber: true,
  });

  const queryClient = useQueryClient();

  const dynamicInvoiceId = useParamsDynamicInvoiceId();
  const updateLineItemMutation = useUpdateLineItem();

  const onSubmit = form.handleSubmit(async ({ amount: newAmount }) => {
    const tiersUsageMap = buildTiersUsageMapInput(productVariantId, tierIndex, {
      type: DISCOUNT_TYPES.Fixed,
      amount: newAmount,
    });

    await runMutation(
      updateLineItemMutation,
      { 
        lineItemId,
        updateLineItemRequest: {
          type: LINE_ITEM_TYPES.Usage,
          tiersUsageMap,
        },
      },
      {
        pending: 'Updating line item...',
        success: 'Line item updated',
        error: 'Failed to update line item',
      },
      {
        onSuccess: () => queryClient.invalidateQueries(['dynamicInvoice', dynamicInvoiceId]),
      });
  });

  useEffect(() => {
    if (!editMode) {
      form.reset({ amount });
    }
  }, [editMode]);

  return (
    <FormProvider {...form}>
      <form
        onSubmit={onSubmit}
        className="m-auto flex w-fit"
      >
        {children(discountRegister)}
      </form>
    </FormProvider>
  );
};

function buildTiersUsageMapInput(productVariantId: string, tierIndex: number, discountConfiguration: TierDiscountConfiguration): TiersUsageMapInput {
  return {
    [productVariantId]: [{
      tierIndex,
      discountConfiguration,
    }],
  };  
}